import React, { useState, useEffect } from "react";
import { Edit2, Check, X, Plus } from "lucide-react";
import customFieldsService from "../services/customFieldsService";
import { CustomFieldDefinition, CustomFieldValues } from "../types/customFields";
import { renderInput } from "./CustomFieldInput";
import InlineFieldCreator from "./InlineFieldCreator";

interface SubscriberCustomFieldsProps {
  subscriberId: string;
  customFields?: Record<string, string | number | boolean | null>;
  onUpdate?: (fields: CustomFieldValues) => void;
}

const SubscriberCustomFields: React.FC<SubscriberCustomFieldsProps> = ({
  subscriberId,
  customFields = {},
  onUpdate,
}) => {
  const [definitions, setDefinitions] = useState<CustomFieldDefinition[]>([]);
  const [values, setValues] = useState<Record<string, string | number | boolean | null>>({});
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  // Add field form
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    loadDefinitions();
  }, []);

  useEffect(() => {
    setValues(customFields || {});
  }, [customFields]);

  const loadDefinitions = async () => {
    try {
      const defs = await customFieldsService.listDefinitions();
      setDefinitions(defs);
    } catch (err) {
      console.error("Failed to load field definitions:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      console.log("[CustomFields] Saving:", { subscriberId, values });
      const result = await customFieldsService.updateSubscriberFields(subscriberId, values);
      console.log("[CustomFields] Response:", result);
      setValues(result.subscriber?.custom_fields || values);
      setEditing(false);
      onUpdate?.(result.subscriber?.custom_fields || values);
    } catch (err: any) {
      console.error("[CustomFields] Save error:", err);
      setError(err.response?.data?.error || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setValues(customFields || {});
    setEditing(false);
    setError("");
  };

  const updateValue = (fieldId: string | number, value: string | number | boolean | null) => {
    setValues((prev) => ({ ...prev, [String(fieldId)]: value }));
  };

  const handleFieldCreated = async (def: CustomFieldDefinition) => {
    setDefinitions((prev) => [...prev, def]);
    setShowAddForm(false);
  };

  const formatDisplayValue = (def: CustomFieldDefinition, val: string | number | boolean | null | undefined): string => {
    if (val === null || val === undefined) return "—";
    if (def.field_type === "boolean") return val === true || val === "true" ? "Yes" : "No";
    return String(val);
  };

  if (loading) return null;

  return (
    <div
      style={{
        marginBottom: "24px",
        padding: "16px",
        backgroundColor: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
        <h3 style={{ margin: 0, fontSize: "16px" }}>Custom Fields</h3>
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          {!editing && !showAddForm && (
            <>
              <button
                onClick={() => setShowAddForm(true)}
                style={{ padding: "4px 8px", border: "none", background: "none", cursor: "pointer", color: "#6b7280", display: "flex", alignItems: "center", gap: "4px", fontSize: "13px" }}
              >
                <Plus size={14} /> Add Field
              </button>
              {definitions.length > 0 && (
                <button
                  onClick={() => setEditing(true)}
                  style={{ padding: "4px 8px", border: "none", background: "none", cursor: "pointer", color: "#6b7280", display: "flex", alignItems: "center", gap: "4px", fontSize: "13px" }}
                >
                  <Edit2 size={14} /> Edit
                </button>
              )}
            </>
          )}
          {editing && (
            <>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{ padding: "4px 8px", border: "none", background: "none", cursor: "pointer", color: "#10b981", display: "flex", alignItems: "center", gap: "4px", fontSize: "13px" }}
              >
                <Check size={14} /> {saving ? "Saving..." : "Save"}
              </button>
              <button
                onClick={handleCancel}
                style={{ padding: "4px 8px", border: "none", background: "none", cursor: "pointer", color: "#6b7280", display: "flex", alignItems: "center", gap: "4px", fontSize: "13px" }}
              >
                <X size={14} /> Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {error && <div style={{ color: "red", fontSize: "13px", marginBottom: "8px" }}>{error}</div>}

      {/* Add field inline form */}
      {showAddForm && (
        <div style={{ marginBottom: "12px" }}>
          <InlineFieldCreator
            onFieldCreated={handleFieldCreated}
            onCancel={() => { setShowAddForm(false); setError(""); }}
          />
        </div>
      )}

      {definitions.length === 0 && !showAddForm ? (
        <p style={{ margin: 0, fontSize: "13px", color: "#9ca3af" }}>
          No custom fields defined yet. Click "Add Field" to create one.
        </p>
      ) : definitions.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {definitions.map((def) => {
            const key = String(def.id);
            return (
              <div key={key} style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                <span style={{ fontSize: "12px", color: "#6b7280", fontWeight: 500 }}>{def.name}</span>
                {editing ? (
                  renderInput(def, values[key], (val) => updateValue(key, val))
                ) : (
                  <span style={{ fontSize: "14px", color: "#111827" }}>
                    {formatDisplayValue(def, values[key])}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SubscriberCustomFields;
